import React, { useMemo, useCallback, useState } from "react";
import {
  Plus,
  SquaresFour,
  HardHat,
  Link,
  ArrowRight,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useNavigate } from "react-router-dom";
import { RenovationProject } from "shared/types/renovation";
import { organizeProjectsByYear } from "../../utils/projectOrganizer";
import { ProjectKanbanView } from "./ProjectKanbanView";
import { ProjectCardSettingsPopover } from "./ProjectCardSettingsPopover";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { NewRenovationProjectModal } from "screens/renovationProject/NewRenovationProjectModal";

interface ProjectsSectionProps {
  buildingId: string;
  projects: RenovationProject[];
  loading: boolean;
  onUpdate: () => void;
  highlightProjectId?: number | null;
  documents?: any[];
  totalDocumentsCount?: number;
  hasMoreDocuments?: boolean;
  isLoadingMoreDocuments?: boolean;
  onLoadMoreDocuments?: () => void;
}

export const ProjectsSection = React.memo(
  ({
    buildingId,
    projects,
    loading,
    onUpdate,
    highlightProjectId,
    documents = [],
    totalDocumentsCount = 0,
    hasMoreDocuments = false,
    isLoadingMoreDocuments = false,
  }: ProjectsSectionProps) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);

    const handleOpenAddProjectModal = useCallback(() => {
      setCreateOpen(true);
    }, []);

    const handleViewAllDocuments = useCallback(() => {
      console.log("Navigating to documents page with:", {
        buildingId,
        documentsCount: documents.length,
        totalCount: totalDocumentsCount,
      });

      // Use 'building' parameter to match FilterBar convention
      navigate(`/renovation/documents?building=${buildingId}`, {
        state: {
          documents,
          totalCount: totalDocumentsCount,
          hasMore: hasMoreDocuments,
          isLoadingMore: isLoadingMoreDocuments,
          buildingId,
        },
      });
    }, [
      navigate,
      buildingId,
      documents,
      totalDocumentsCount,
      hasMoreDocuments,
      isLoadingMoreDocuments,
    ]);

    // Get warnings from organized projects (for display purposes)
    const projectWarnings = useMemo(() => {
      const organized = organizeProjectsByYear(projects);
      return organized.warnings;
    }, [projects]);

    return (
      <>
      <div className="flex flex-col gap-3">
        <header className="flex justify-between">
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            {isExpanded ? (
              <CaretUp weight="bold" size={16} className="text-neutral-500" />
            ) : (
              <CaretDown weight="bold" size={16} className="text-neutral-500" />
            )}
            <SquaresFour weight="duotone" size={18} />
            <p className="text-lg font-semibold">Projecten</p>
            <span className="text-neutral-secondary-mid text-sm">
              ({projects.length} project{projects.length === 1 ? "" : "en"})
            </span>
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {/* Card Settings */}
            <ProjectCardSettingsPopover />

            {/* View All Documents Button */}
            <ButtonNew
              variant="secondaryMoss"
              icon={<ArrowRight />}
              label="Bekijk alle documenten"
              onClick={handleViewAllDocuments}
            />

            <Dropdown>
              <DropdownTrigger>
                <div>
                  <ButtonNew
                    variant="secondaryNeutralMid"
                    icon={<Link weight="duotone" size={16} />}
                    label="Externe Links"
                  />
                </div>
              </DropdownTrigger>
              <DropdownMenu aria-label="Externe links">
                <DropdownItem
                  key="livios"
                  startContent={<Link weight="duotone" size={16} />}
                  onPress={() =>
                    window.open(
                      "https://livios.be",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Livios
                </DropdownItem>
                <DropdownItem
                  key="solvari"
                  startContent={<Link weight="duotone" size={16} />}
                  onPress={() =>
                    window.open(
                      "https://solvari.be",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Solvari
                </DropdownItem>
                <DropdownItem
                  key="trustup"
                  startContent={<Link weight="duotone" size={16} />}
                  onPress={() =>
                    window.open(
                      "https://trustup.be",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  TrustUp
                </DropdownItem>
                <DropdownItem
                  key="check-inhoudingsplicht"
                  startContent={<Link weight="duotone" size={16} />}
                  onPress={() =>
                    window.open(
                      "https://www.checkinhoudingsplicht.be",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  Check inhoudingsplicht
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
            <ButtonNew
              onClick={handleOpenAddProjectModal}
              icon={<Plus />}
              label="Project toevoegen"
              variant="primaryMoss"
            />
          </div>
        </header>
        {isExpanded && (loading ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <div className="flex items-center gap-3">
              <div className="border-moss-primary/20 border-t-moss-primary h-8 w-8 animate-spin rounded-full border-4"></div>
              <HardHat
                weight="duotone"
                size={28}
                className="text-moss-primary animate-bounce"
              />
            </div>
            <div className="text-center">
              <p className="text-neutral-primary text-lg font-medium">
                Projecten aan het laden
              </p>
              <p className="text-sm text-neutral-600">
                Even geduld terwijl we je projecten ophalen...
              </p>
            </div>
          </div>
        ) : projects.length === 0 ? (
          <div className="py-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-neutral-100">
                <SquaresFour
                  weight="duotone"
                  size={32}
                  className="text-neutral-400"
                />
              </div>
              <div>
                <p className="text-neutral-primary text-lg font-medium">
                  Geen projecten gevonden
                </p>
                <p className="text-sm text-neutral-600">
                  Probeer andere filters of begin met het toevoegen van een
                  nieuw project
                </p>
              </div>
            </div>
          </div>
        ) : (
          <ProjectKanbanView
            projects={projects}
            warnings={projectWarnings}
            buildingId={buildingId}
            onUpdate={onUpdate}
            highlightProjectId={highlightProjectId}
          />
        ))}
      </div>

      <NewRenovationProjectModal
        isOpen={createOpen}
        buildingId={buildingId}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          onUpdate();
          setCreateOpen(false);
          // Default modal behaviour navigates to /renovation/:id, which is
          // what we want here too — let it run.
        }}
      />
      </>
    );
  },
);

ProjectsSection.displayName = "ProjectsSection";
